import type { PageContext } from './pageContext.js';

export type SpeakOptions = {
  apiBase: string;
  apiKey: string;
  text: string;
  voice: string;
  speed?: number;
  // Phase 2: optional translation. When `translate` is true and `translateTo`
  // is set, the backend calls Capsiynau /v1/translate before synthesis.
  translate?: boolean;
  translateTo?: 'cy' | 'en';
  // Phase 3b: requested audio container. Default 'opus' (small + good).
  // Player requests 'mp3' when MediaSource supports it, so OpenAI synth
  // can stream straight into MSE without a re-encode hop.
  format?: 'mp3' | 'opus';
  // Phase A page-context: opt-in metadata sent with each call so the
  // backend can bias voice selection + ambiguous-token expansion. Sender
  // is responsible for the toggle gate; if pageContext is set, it ships.
  pageContext?: PageContext;
  signal?: AbortSignal;
};

export type SpeakResult =
  | { kind: 'audio'; response: Response; beacon: string | null }
  | { kind: 'not_implemented'; accepted: Record<string, unknown> }
  | { kind: 'error'; status: number; message: string };

export async function speak(opts: SpeakOptions): Promise<SpeakResult> {
  const body = JSON.stringify({
    text: opts.text,
    voice: opts.voice,
    speed: opts.speed ?? 1.0,
    ...(opts.format ? { format: opts.format } : {}),
    ...(opts.translate && opts.translateTo
      ? { translate: true, targetLang: opts.translateTo }
      : {}),
    ...(opts.pageContext ? { pageContext: opts.pageContext } : {}),
  });

  let response: Response;
  try {
    response = await fetch(`${opts.apiBase}/v1/speak`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'x-llais-key': opts.apiKey,
      },
      body,
      signal: opts.signal,
    });
  } catch (err) {
    return { kind: 'error', status: 0, message: (err as Error).message || 'network_error' };
  }

  if (response.ok) {
    const beacon = response.headers.get('x-llais-beacon');
    return { kind: 'audio', response, beacon };
  }

  if (response.status === 501) {
    try {
      const data = (await response.json()) as { accepted?: Record<string, unknown> };
      return { kind: 'not_implemented', accepted: data.accepted ?? {} };
    } catch {
      return { kind: 'not_implemented', accepted: {} };
    }
  }

  let message = `http_${response.status}`;
  try {
    const data = (await response.json()) as { error?: string };
    if (data.error) message = data.error;
  } catch {
    /* ignore */
  }
  return { kind: 'error', status: response.status, message };
}

export type VoiceCatalogueEntry = {
  id: string;
  label: string;
  // Welsh-mode label served by the backend (e.g. 'Nia (Cymraeg, gogledd)').
  // Optional - if missing, the widget falls back to `label`. Older backend
  // versions without label_cy keep working unchanged.
  label_cy?: string;
  engine: 'azure' | 'piper' | 'openai';
  resolvedEngine: 'azure' | 'piper' | 'openai' | 'dev-mock';
  available: boolean;
  language: string;
  gender?: 'female' | 'male' | 'neutral';
  description?: string;
};

export async function fetchVoices(apiBase: string): Promise<VoiceCatalogueEntry[]> {
  try {
    const r = await fetch(`${apiBase}/v1/voices`);
    if (!r.ok) return [];
    const data = (await r.json()) as { voices?: VoiceCatalogueEntry[] };
    return data.voices ?? [];
  } catch {
    return [];
  }
}

export async function sendBeacon(
  apiBase: string,
  token: string,
  latencyMs: number,
): Promise<void> {
  // Uses fetch with keepalive rather than navigator.sendBeacon: the audio
  // 'ended' event is not an unload-phase event, and sendBeacon does not
  // wait for the CORS preflight before firing the POST, so application/json
  // bodies get blocked. fetch + keepalive does both correctly.
  try {
    await fetch(`${apiBase}/v1/beacon`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ token, latencyMs }),
      keepalive: true,
    });
  } catch {
    /* best-effort, no retry */
  }
}
